/* eslint-disable no-await-in-loop */
import { expect, waitUntil } from '@open-wc/testing';
import { executeServerCommand, sendMouse } from '@web/test-runner-commands';
import fetchMock from 'fetch-mock';
import '../src/testHarness.js';
import {
  apiCallKey,
  mockDataMeta,
  fixtureBeforeEach,
  fixtureBefore,
  fixtureAfterEach,
  TelemInfo,
  screenshotAndCompare,
} from './utils/helpers.js';

describe('ix-notifications', () => {
  before(async () => {
    await fixtureBefore();
  });

  beforeEach(async () => {
    await fixtureBeforeEach();
  });

  afterEach(async () => {
    fixtureAfterEach();
  });

  it('should toggle its visibility when the ix-notifications-toggle-drawer event is triggerd', async () => {
    await screenshotAndCompare('drawer-open');

    let drawInfo: TelemInfo[] = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-drawer"]',
      action: 'info',
    });

    expect(drawInfo[0].attributes.isvisible).to.equal(true);

    window.dispatchEvent(new CustomEvent('ix-notifications-toggle-drawer'));

    drawInfo = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-drawer"]',
      action: 'info',
    });

    await screenshotAndCompare('drawer-closed');

    expect(drawInfo[0].attributes.isvisible).to.equal(undefined);

    window.dispatchEvent(new CustomEvent('ix-notifications-toggle-drawer'));

    drawInfo = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-drawer"]',
      action: 'info',
    });

    expect(drawInfo[0].attributes.isvisible).to.equal(true);
  });

  it('show show tooltips when hovering over action buttons', async () => {
    const activeCtas: TelemInfo[] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: '[data-testclass="notifications-cta"]:not([disabled])',
        action: 'info',
      }
    );

    const ctaTask = activeCtas.map(cta =>
      Promise.resolve(async () => {
        let tooltipInfo: TelemInfo[] = [];

        await waitUntil(
          async () => {
            const position: [number, number] = await executeServerCommand(
              'mouseFocusInfo',
              {
                selector: `[data-testid="${cta.attributes['data-testid']}"]`,
                action: 'mouse',
              }
            );

            await sendMouse({
              type: 'move',
              position,
            });

            tooltipInfo = await executeServerCommand('mouseFocusInfo', {
              selector: 'notification-tooltip',
              action: 'info',
            });

            return tooltipInfo[0].attributes.isvisible;
          },
          'Tooltip is never visible',
          { timeout: 20000 }
        );

        expect(tooltipInfo[0].attributes.isvisible).to.equal(true);
        expect(tooltipInfo[0].attributes.label).to.not.equal(undefined);
        expect((tooltipInfo[0].attributes.label as string).trim()).to.not.equal(
          ''
        );
      })
    );

    for (const task of ctaTask) {
      await (
        await task
      )();
    }
  });

  it(`it renders earlier and today items given the mock data`, async () => {
    await waitUntil(
      () => fetchMock.callHistory.calls(apiCallKey).length > 0,
      `${apiCallKey} API call never made`,
      { interval: 10, timeout: 20000 }
    );

    await waitUntil(
      async () => {
        const itemsInfo: TelemInfo[] = await executeServerCommand(
          'mouseFocusInfo',
          {
            selector: 'notification-item',
            action: 'info',
          }
        );

        return itemsInfo.length > 0;
      },
      'No notification items rendered',
      { interval: 10, timeout: 20000 }
    );

    const earlierItemsInfo: TelemInfo[] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: '[data-testclass="earlier-notification"]',
        action: 'info',
      }
    );

    expect(earlierItemsInfo.length).to.equal(mockDataMeta.earlierItemsCount);

    const todayItemsInfo: TelemInfo[] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: '[data-testclass="today-notification"]',
        action: 'info',
      }
    );

    expect(todayItemsInfo.length).to.equal(mockDataMeta.todayItemsCount);
  });
});
